import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useIsPWA } from '../contexts/PWAContext';

const PWAUpdatePrompt: React.FC = () => {
  const [updating, setUpdating] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const isPWA = useIsPWA();

  useEffect(() => {
    // Wait for PWA context to be properly initialized
    // This prevents race conditions where the context might not be ready
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100); // Small delay to ensure context is ready

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only run PWA update detection if we're in PWA mode and context is initialized
    if (!isInitialized) {
      console.log('PWA Update: Context not yet initialized, waiting...');
      return;
    }

    if (!isPWA) {
      console.log('PWA Update: Not in PWA mode, skipping update detection');
      return;
    }

    console.log(
      'PWA Update: Context initialized and in PWA mode, starting update detection',
    );

    if ('serviceWorker' in navigator) {
      let refreshing = false;

      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            // Check if there's a waiting worker
            if (registration.waiting) {
              console.log('PWA Update: Found waiting worker');
              setUpdateAvailable(true);
              return;
            }

            // Check if there's an installing worker
            if (registration.installing) {
              console.log('PWA Update: Found installing worker');
              registration.installing.addEventListener('statechange', () => {
                if (registration.installing?.state === 'installed') {
                  console.log('PWA Update: Worker installed, waiting for user');
                  setUpdateAvailable(true);
                }
              });
            }
          }
        } catch (error) {
          console.error('PWA Update: Error checking for updates', error);
        }
      };

      // Listen for new service worker installations
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('PWA Update: Controller changed');
        if (!refreshing) {
          refreshing = true;
          setUpdating(false);
          setUpdated(true);
          // Reload after a short delay to show the success message
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      });

      // Check for updates when the component mounts
      checkForUpdates();

      // Set up periodic update checks (every 5 minutes)
      const updateCheckInterval = setInterval(checkForUpdates, 5 * 60 * 1000);

      // Clean up interval on unmount
      return () => {
        clearInterval(updateCheckInterval);
      };
    }
  }, [isPWA, isInitialized]);

  const handleClose = () => {
    setUpdated(false);
    window.location.reload();
  };

  const handleUpdateNow = () => {
    setUpdateAvailable(false);
    // Get the waiting service worker and trigger the update
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration && registration.waiting) {
        console.log('PWA Update: Triggering update now');
        setUpdating(true);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        // If no waiting worker, just reload to get the latest version
        window.location.reload();
      }
    });
  };

  // Don't render anything until context is initialized and we're in PWA mode
  if (!isInitialized || !isPWA) {
    return null;
  }

  if (updating) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            py={3}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" align="center">
              Applying application updates...
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Please wait while we update to the latest version.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (updated) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            py={3}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="h6" align="center">
              App Updated Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              The application has been updated with the latest improvements and
              bug fixes.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (updateAvailable) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            py={3}
          >
            <Typography variant="h6" align="center">
              Update Available
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              A new version of the application is available. Would you like to
              update now?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpdateNow} variant="contained" color="primary">
            Update Now
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

export default PWAUpdatePrompt;
